/**
 * TaskDetailModal — the ClickUp-style task detail view (z-[200]).
 *
 * Inline title, markdown description, status + priority selectors, assignee
 * editor, due date + reminder, entity link, watchers, subtasks, and an activity
 * / comment timeline. Every mutation goes through todo.service (which logs a
 * TaskActivity entry); assignment + comment @mentions fire the bell + push via
 * createMentions.
 *
 * The `task` prop must be the LIVE task (resolved by id from the page's
 * subscription) so edits reflect immediately.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Calendar,
  Bell,
  Plus,
  Trash2,
  Check,
  Eye,
  EyeOff,
  ExternalLink,
  CornerDownLeft,
  UserPlus,
  Pencil,
} from 'lucide-react';
import type { TodoItem, TaskActivity } from '@/types/schema';
import type { MentionableUser } from '@/types/mention.types';
import { MentionEntityType } from '@/types/mention.types';
import {
  STATUS_ORDER,
  PRIORITY_ORDER,
  getStatusTheme,
  getPriorityTheme,
  getEntityTheme,
} from '@/config/entityThemes';
import { EntityIcon } from '@/components/common/EntityIcon';
import { Avatar } from '@/components/common/Avatar';
import { MarkdownDisplay } from '@/components/MarkdownDisplay';
import { useTaskActivity } from '@/hooks/useTaskActivity';
import { useAutoReadMentions } from '@/hooks/useAutoReadMentions';
import {
  updateTodo,
  updateTaskStatus,
  updateTaskPriority,
  setTaskDueDate,
  setTaskReminder,
  addTaskAssignee,
  removeTaskAssignee,
  addWatcher,
  removeWatcher,
  addTaskComment,
  createTodo,
  toggleTodo,
  deleteTodo,
} from '@/services/todo.service';
import { notifyTaskAssignees } from '@/services/task-notify';
import { createMentions } from '@/services/mention.service';
import { getLocaleSync } from '@/utils/locale';
import { AutobuildDecisionCard } from '@/components/bug-reports/AutobuildDecisionCard';

function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString(getLocaleSync(), { day: 'numeric', month: 'short' });
}

interface TeamLite {
  id: string;
  name: string;
  role: string;
  dualAccess?: boolean;
}

interface TaskDetailModalProps {
  task: TodoItem;
  childTasks: TodoItem[];
  availableUsers: MentionableUser[];
  team: TeamLite[];
  actor: { id: string; name: string; role: string };
  tenantId: string;
  onClose: () => void;
  onOpenTask?: (taskId: string) => void;
  /** Route change from inside the modal (entity link). The overlay host uses
   *  this to close the expanded panel before navigating. */
  onNavigate?: (path: string) => void;
}

export function TaskDetailModal({
  task,
  childTasks,
  availableUsers,
  team,
  actor,
  tenantId,
  onClose,
  onOpenTask,
  onNavigate,
}: TaskDetailModalProps) {
  const navigate = useNavigate();
  const taskId = task.id!;
  const { entries } = useTaskActivity(taskId);

  // Opening the task reads every @mention pointing at it (reminders, tag-ins).
  useAutoReadMentions(taskId);

  const [tab, setTab] = useState<'details' | 'activity'>('details');
  const [title, setTitle] = useState(task.title);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(task.description || '');
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [comment, setComment] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [subtaskTitle, setSubtaskTitle] = useState('');

  useEffect(() => setTitle(task.title), [task.title]);
  useEffect(() => {
    if (!editingDesc) setDescDraft(task.description || '');
  }, [task.description, editingDesc]);

  // Keep the denormalised subtask counts self-healing from live children.
  const childSyncRef = useRef('');
  useEffect(() => {
    const total = childTasks.length;
    const done = childTasks.filter((t) => t.status === 'done').length;
    const key = `${total}:${done}`;
    if (childSyncRef.current === key) return;
    childSyncRef.current = key;
    if ((task.subtaskCount ?? 0) !== total || (task.subtaskDoneCount ?? 0) !== done) {
      updateTodo(taskId, { subtaskCount: total, subtaskDoneCount: done }).catch(() => {});
    }
  }, [childTasks, task.subtaskCount, task.subtaskDoneCount, taskId]);

  // Escape to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Disarm the two-tap delete confirm after 3s so a stray first tap can't
  // leave a live "Confirm delete" button lying around.
  useEffect(() => {
    if (!confirmDelete) return;
    const t = setTimeout(() => setConfirmDelete(false), 3000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const entityTheme = getEntityTheme(task.entityKind);
  const assignees = task.assignees ?? [];
  const assignedIds = new Set(assignees.map((a) => a.userId));
  const isWatching = (task.watcherIds ?? []).includes(actor.id);
  const unassignedTeam = useMemo(
    () => team.filter((m) => !assignedIds.has(m.id)),
    [team, assignedIds],
  );

  const saveTitle = () => {
    const t = title.trim();
    if (t && t !== task.title) updateTodo(taskId, { title: t }).catch(() => {});
  };

  const saveDesc = () => {
    updateTodo(taskId, { description: descDraft.trim() }).catch(() => {});
    setEditingDesc(false);
  };

  // Transactional ops — concurrent edits from two clients serialize instead
  // of clobbering each other, and both assignee mirrors are recomputed from
  // the live doc (legacy docs + any duplicates self-heal on touch).
  const addAssignee = (m: TeamLite) => {
    const now = new Date().toISOString();
    const role = m.dualAccess ? 'Office' : m.role || 'Office';
    addTaskAssignee(taskId, { userId: m.id, userName: m.name, role, addedAt: now }, actor)
      .then((added) => {
        // Only notify when this click actually added them (a concurrent add
        // by someone else must not double-ping).
        if (added) {
          notifyTaskAssignees({
            taskId,
            title: task.title,
            assigneeUserIds: [m.id],
            actor: { id: actor.id, name: actor.name },
            tenantId,
            availableUsers,
          }).catch(() => {});
        }
      })
      .catch(() => {});
    setShowAssignMenu(false);
  };

  const removeAssignee = (userId: string) => {
    const existing = assignees.find((a) => a.userId === userId);
    removeTaskAssignee(taskId, userId, existing?.userName || 'Assignee', actor).catch(() => {});
  };

  const submitComment = async () => {
    const body = comment.trim();
    if (!body) return;
    setComment('');
    await addTaskComment(taskId, { authorId: actor.id, authorName: actor.name, body }).catch(() => {});
    // Notify anyone @mentioned in the comment.
    createMentions({
      noteContent: body,
      entityType: MentionEntityType.TODO,
      entityId: taskId,
      entityTitle: `Task: ${task.title.slice(0, 60)}`,
      createdBy: actor.id,
      createdByName: actor.name,
      tenantId,
      linkPath: `/home/plate?taskId=${taskId}`,
      availableUsers,
    }).catch(() => {});
  };

  const addSubtask = () => {
    const t = subtaskTitle.trim();
    if (!t) return;
    setSubtaskTitle('');
    createTodo({
      tenantId,
      title: t,
      completed: false,
      status: 'todo',
      priority: task.priority ?? 'normal',
      entityKind: task.entityKind ?? 'general',
      parentTaskId: taskId,
      order: childTasks.length,
      createdBy: actor.id,
      createdByName: actor.name,
      assignees: assignees.length ? assignees : [{ userId: actor.id, userName: actor.name, role: actor.role, addedAt: new Date().toISOString() }],
      ...(task.sourceType ? { sourceType: task.sourceType } : {}),
      ...(task.sourceId ? { sourceId: task.sourceId } : {}),
      ...(task.sourceLabel ? { sourceLabel: task.sourceLabel } : {}),
      ...(task.sourcePath ? { sourcePath: task.sourcePath } : {}),
    }).catch(() => {});
  };

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteTodo(taskId).catch(() => {});
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[200] flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-surface-card border border-theme sm:rounded-2xl shadow-2xl w-full max-w-3xl min-h-screen sm:min-h-0 my-0 sm:my-6"
        >
          {/* Header */}
          <div className="flex items-start gap-3 p-4 border-b border-theme-soft">
            <EntityIcon kind={task.entityKind} size="md" />
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveTitle();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              aria-label="Task title"
              className="flex-1 min-w-0 bg-transparent text-lg font-bold text-theme-primary focus:outline-none border-b border-transparent focus:border-theme-soft"
            />
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 p-1.5 rounded-lg text-theme-muted hover:text-theme-primary hover:bg-surface-hover"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3">
            {/* Main column — on mobile the properties sidebar renders FIRST
                (status/priority/assignees are the primary actions on a phone;
                a long description must not bury them below the fold) */}
            <div className="order-2 md:order-1 md:col-span-2 p-4 md:border-r border-theme-soft">
              {/* Tabs */}
              <div className="flex gap-1 bg-surface-raised rounded-xl p-1 w-fit mb-4">
                {(['details', 'activity'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg capitalize transition-all ${
                      tab === t ? 'bg-surface-card text-theme-primary shadow-sm' : 'text-theme-muted hover:text-theme-secondary'
                    }`}
                  >
                    {t}
                    {t === 'activity' && entries.length > 0 && (
                      <span className="ml-1.5 text-[10px] text-theme-muted">{entries.length}</span>
                    )}
                  </button>
                ))}
              </div>

              {tab === 'details' ? (
                <div className="space-y-5">
                  {/* Description */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-theme-secondary uppercase tracking-wide">Description</span>
                      {!editingDesc && (
                        <button
                          type="button"
                          onClick={() => setEditingDesc(true)}
                          className="text-[11px] text-theme-muted hover:text-brand-secondary flex items-center gap-1"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                      )}
                    </div>
                    {editingDesc ? (
                      <div>
                        <textarea
                          value={descDraft}
                          onChange={(e) => setDescDraft(e.target.value)}
                          rows={5}
                          placeholder="Add details… (markdown supported)"
                          className="w-full bg-surface-input border border-theme rounded-xl px-3 py-2 text-sm text-theme-primary focus:outline-none focus:ring-2 focus:ring-brand-secondary/40 resize-y"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            onClick={saveDesc}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-brand-secondary text-brand-primary"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingDesc(false);
                              setDescDraft(task.description || '');
                            }}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg text-theme-muted hover:bg-surface-hover"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : task.description ? (
                      <MarkdownDisplay content={task.description} compact />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingDesc(true)}
                        className="text-sm text-theme-muted italic hover:text-theme-secondary"
                      >
                        Add a description…
                      </button>
                    )}
                  </div>

                  {/* A task the autobuild pipeline raised about a bug report:
                      answer its question / approve the build right here.
                      Renders nothing for any other task. */}
                  <AutobuildDecisionCard sourcePath={task.sourcePath} viewer={{ id: actor.id, name: actor.name }} />

                  {/* Subtasks */}
                  <div>
                    <span className="text-xs font-bold text-theme-secondary uppercase tracking-wide">
                      Subtasks{childTasks.length > 0 && ` · ${childTasks.filter((c) => c.status === 'done').length}/${childTasks.length}`}
                    </span>
                    <div className="space-y-1.5 mt-1.5">
                      {childTasks.map((c) => (
                        <div key={c.id} className="flex items-center gap-2 group">
                          <button
                            type="button"
                            onClick={() => c.id && toggleTodo(c.id, c.status !== 'done', actor.id).catch(() => {})}
                            className="-m-2 p-2 shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
                            aria-label={c.status === 'done' ? 'Mark subtask not done' : 'Mark subtask done'}
                          >
                            <span
                              className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                                c.status === 'done'
                                  ? 'bg-emerald-500 border-emerald-500 text-white'
                                  : 'border-theme hover:border-emerald-400 text-transparent'
                              }`}
                            >
                              <Check className="w-2.5 h-2.5" strokeWidth={3} />
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => c.id && onOpenTask?.(c.id)}
                            className={`text-sm text-left flex-1 min-w-0 truncate ${
                              c.status === 'done' ? 'line-through text-theme-muted' : 'text-theme-primary hover:text-brand-secondary'
                            }`}
                          >
                            {c.title}
                          </button>
                        </div>
                      ))}
                      <div className="flex items-center gap-2 pt-1">
                        <Plus className="w-4 h-4 text-theme-muted shrink-0" />
                        <input
                          value={subtaskTitle}
                          onChange={(e) => setSubtaskTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') addSubtask();
                          }}
                          placeholder="Add a subtask…"
                          className="flex-1 min-w-0 bg-transparent text-sm text-theme-primary placeholder:text-theme-muted focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Activity tab */
                <div className="space-y-4">
                  <div className="flex items-start gap-2">
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitComment();
                      }}
                      rows={2}
                      placeholder="Add a comment… @mention to notify. ⌘↵ to send"
                      className="flex-1 bg-surface-input border border-theme rounded-xl px-3 py-2 text-sm text-theme-primary focus:outline-none focus:ring-2 focus:ring-brand-secondary/40 resize-y"
                    />
                    <button
                      type="button"
                      onClick={submitComment}
                      disabled={!comment.trim()}
                      aria-label="Send comment"
                      className={`shrink-0 p-2.5 rounded-xl transition-colors ${
                        comment.trim() ? 'bg-brand-secondary text-brand-primary' : 'bg-surface-hover text-theme-muted/50'
                      }`}
                    >
                      <CornerDownLeft className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    {entries.length === 0 && (
                      <p className="text-xs text-theme-muted text-center py-4">No activity yet.</p>
                    )}
                    {[...entries].reverse().map((entry) => (
                      <ActivityRow key={entry.id} entry={entry} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar — properties */}
            <div className="order-1 md:order-2 p-4 space-y-4 border-b md:border-b-0 border-theme-soft">
              {/* Status */}
              <PropGroup label="Status">
                <div className="grid grid-cols-2 gap-1.5">
                  {STATUS_ORDER.map((s) => {
                    const t = getStatusTheme(s);
                    const active = (task.status ?? 'todo') === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => updateTaskStatus(taskId, s, actor, task.status).catch(() => {})}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                          active ? `${t.badge} shadow-sm` : 'bg-surface-raised text-theme-muted hover:text-theme-secondary'
                        }`}
                        aria-pressed={active}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </PropGroup>

              {/* Priority */}
              <PropGroup label="Priority">
                <div className="grid grid-cols-2 gap-1.5">
                  {PRIORITY_ORDER.map((p) => {
                    const t = getPriorityTheme(p);
                    const active = (task.priority ?? 'normal') === p;
                    const Icon = t.Icon;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => updateTaskPriority(taskId, p, actor).catch(() => {})}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                          active ? `${t.badge} shadow-sm` : 'bg-surface-raised text-theme-muted hover:text-theme-secondary'
                        }`}
                        aria-pressed={active}
                      >
                        <Icon className="w-3 h-3" />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </PropGroup>

              {/* Assignees */}
              <PropGroup label="Assignees">
                <div className="flex flex-wrap gap-1.5 items-center">
                  {assignees.map((a) => (
                    <span key={a.userId} className="inline-flex items-center gap-1 bg-surface-raised rounded-full pl-1 pr-2 py-0.5 group">
                      <Avatar name={a.userName} userId={a.userId} size="xs" />
                      <span className="text-[11px] text-theme-secondary">{a.userName.split(' ')[0]}</span>
                      <button
                        type="button"
                        onClick={() => removeAssignee(a.userId)}
                        className="text-theme-muted hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowAssignMenu((v) => !v)}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-theme-muted hover:text-brand-secondary bg-surface-raised rounded-full px-2 py-1"
                    >
                      <UserPlus className="w-3 h-3" /> Assign
                    </button>
                    {showAssignMenu && (
                      <div className="absolute right-0 mt-1 w-48 max-h-56 overflow-y-auto rounded-xl border border-theme bg-surface-card shadow-xl z-10 py-1">
                        {unassignedTeam.length === 0 && (
                          <p className="text-[11px] text-theme-muted px-3 py-2">Everyone's assigned.</p>
                        )}
                        {unassignedTeam.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => addAssignee(m)}
                            className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-surface-hover text-left"
                          >
                            <Avatar name={m.name} userId={m.id} size="xs" />
                            <span className="text-xs text-theme-primary truncate">{m.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </PropGroup>

              {/* Due + reminder */}
              <PropGroup label="Dates">
                <label className="flex items-center gap-2 text-xs text-theme-secondary">
                  <Calendar className="w-3.5 h-3.5 text-theme-muted shrink-0" aria-hidden="true" />
                  <input
                    type="date"
                    value={task.dueDate || ''}
                    onChange={(e) => setTaskDueDate(taskId, e.target.value || null, actor).catch(() => {})}
                    aria-label="Due date"
                    className="flex-1 bg-surface-input border border-theme rounded-lg px-2 py-1.5 text-xs text-theme-primary focus:outline-none"
                  />
                </label>
                <label className="flex items-center gap-2 text-xs text-theme-secondary mt-1.5">
                  <Bell className="w-3.5 h-3.5 text-theme-muted shrink-0" aria-hidden="true" />
                  <input
                    type="datetime-local"
                    value={toLocalInput(task.reminderAt)}
                    onChange={(e) =>
                      setTaskReminder(taskId, e.target.value ? new Date(e.target.value).toISOString() : null).catch(() => {})
                    }
                    aria-label="Reminder time"
                    className="flex-1 bg-surface-input border border-theme rounded-lg px-2 py-1.5 text-xs text-theme-primary focus:outline-none"
                  />
                </label>
              </PropGroup>

              {/* Entity link */}
              {task.sourcePath && (
                <PropGroup label="Linked to">
                  <button
                    type="button"
                    onClick={() =>
                      onNavigate ? onNavigate(task.sourcePath!) : navigate(task.sourcePath!)
                    }
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold ${entityTheme.badge} hover:brightness-110`}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {task.sourceLabel || entityTheme.label}
                  </button>
                </PropGroup>
              )}

              {/* Watch + delete */}
              <div className="flex items-center justify-between pt-2 border-t border-theme-soft">
                <button
                  type="button"
                  onClick={() =>
                    (isWatching ? removeWatcher(taskId, actor.id) : addWatcher(taskId, actor.id)).catch(() => {})
                  }
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-theme-muted hover:text-theme-secondary"
                >
                  {isWatching ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {isWatching ? 'Unwatch' : 'Watch'}
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${
                    confirmDelete ? 'text-red-400' : 'text-theme-muted hover:text-red-400'
                  }`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {confirmDelete ? 'Confirm delete' : 'Delete'}
                </button>
              </div>

              <p className="text-[10px] text-theme-muted/70">
                Created by {task.createdByName} · {relTime(task.createdAt)}
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function PropGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="text-[10px] font-bold text-theme-muted uppercase tracking-wide">{label}</span>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ActivityRow({ entry }: { entry: TaskActivity }) {
  const isComment = entry.type === 'comment';
  const verb = (() => {
    switch (entry.type) {
      case 'status_change':
        return `moved to ${entry.meta?.to ?? ''}`;
      case 'priority_change':
        return `set priority ${entry.meta?.to ?? ''}`;
      case 'assignee_change':
        return `set assignees: ${entry.meta?.to ?? ''}`;
      case 'due_change':
        return `set due ${entry.meta?.to ?? ''}`;
      case 'reminder':
        return 'reminder fired';
      case 'created':
        return 'created this task';
      default:
        return '';
    }
  })();

  return (
    <div className="flex items-start gap-2">
      <Avatar name={entry.authorName || 'System'} userId={entry.authorId} size="xs" />
      <div className="flex-1 min-w-0">
        {isComment ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-semibold text-theme-primary">{entry.authorName}</span>
              <span className="text-[10px] text-theme-muted">{relTime(entry.createdAt)}</span>
            </div>
            <div className="mt-0.5 text-sm text-theme-secondary">
              <MarkdownDisplay content={entry.body || ''} compact highlightMentions />
            </div>
          </>
        ) : (
          <p className="text-[11px] text-theme-muted">
            <span className="font-semibold text-theme-secondary">{entry.authorName}</span> {verb}
            <span className="ml-1.5 text-theme-muted/70">· {relTime(entry.createdAt)}</span>
          </p>
        )}
      </div>
    </div>
  );
}
